import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { getNameFromId, getSlugFromId } from '../../config/mappings';
import { appConfig } from '../../config/appConfig';
import { FullProvince } from '../../hooks/standard/useGetLocationTree';

interface BreadcrumbItem {
    name: string;
    path: string;
}

export interface BreadcrumbFilters {
    contract?: number[];
    type?: number[];
    provincia?: string;
    comune?: string[];
    frazione?: number[];
    quartiere?: number[];
}

interface BreadcrumbProps {
    filters: BreadcrumbFilters | null;
    locationTree: FullProvince[];
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ filters, locationTree }) => {
    const { t, i18n } = useTranslation();
    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';

    const langPrefix = appConfig.integrations.multilang ? `/${i18n.language}` : '';

    const homePath = `${langPrefix}/`;
    const immobiliPath = `${langPrefix}/immobili`;

    const items: BreadcrumbItem[] = [
        { name: t('common.home', 'Home') || 'Home', path: homePath },
        { name: t('propertyList.pageTitle', 'Immobili') || 'Immobili', path: immobiliPath }
    ];

    let currentPath = immobiliPath;

    // Variabili per tenere traccia delle località trovate
    let provinceFound: FullProvince | undefined;
    let comuneFound: FullProvince['comuni'][0] | undefined;

    if (filters) {
        // --- CONTRATTO ---
        if (filters.contract?.length) {
            const contractId = filters.contract[0];
            const contractName = getNameFromId('contratto', contractId);
            const contractSlug = getSlugFromId('contratto', contractId);
            if (contractName && contractSlug) {
                currentPath += `/${contractSlug}`;
                items.push({ name: contractName, path: currentPath });
            }
        }
        // --- TIPOLOGIA ---
        if (filters.type?.length) {
            const typeId = filters.type[0];
            const typeName = getNameFromId('tipologia', typeId);
            const typeSlug = getSlugFromId('tipologia', typeId);
            if (typeName && typeSlug) {
                currentPath += `/${typeSlug}`;
                items.push({ name: typeName, path: currentPath });
            }
        }
        // --- PROVINCIA ---
        if (filters.provincia && locationTree) {
            provinceFound = locationTree.find(p => p.codiceIstat === filters.provincia);
            if (provinceFound?.nome && provinceFound?.slug) {
                currentPath += `/${provinceFound.slug}`;
                items.push({ name: provinceFound.nome, path: currentPath });
            }
        }
        // --- COMUNE ---
        if (filters.comune?.length && provinceFound) {
            comuneFound = provinceFound.comuni.find(c => c.codiceIstat === filters.comune?.[0]);
            if (comuneFound?.nome && comuneFound?.slug) {
                currentPath += `/${comuneFound.slug}`;
                items.push({ name: comuneFound.nome, path: currentPath });
            }
        }
        // --- FRAZIONE O QUARTIERE ---
        if (comuneFound) {
            if (filters.frazione?.length) {
                const frazioneFound = comuneFound.frazioni?.find(f => f.id === filters.frazione?.[0]);
                if (frazioneFound?.nome && frazioneFound?.slug) {
                    currentPath += `/${frazioneFound.slug}`;
                    items.push({ name: frazioneFound.nome, path: currentPath });
                }
            } else if (filters.quartiere?.length) {
                const quartiereFound = comuneFound.quartieri?.find(q => q.id === filters.quartiere?.[0]);
                if (quartiereFound?.nome && quartiereFound?.slug) {
                    currentPath += `/${quartiereFound.slug}`;
                    items.push({ name: quartiereFound.nome, path: currentPath });
                }
            }
        }
    }

    // Genera i dati strutturati (JSON-LD) per la SEO
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": items.map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.name,
            "item": `${currentOrigin}${item.path}`
        }))
    };

    return (
        <>
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            </Helmet>
            <nav aria-label="breadcrumb" className="breadcrumb-nav">
                <ol className="breadcrumb">
                    {items.map((item, index) => (
                        <li key={index} className={`breadcrumb-item ${index === items.length - 1 ? 'active' : ''}`}>
                            {index === items.length - 1 ? (
                                item.name
                            ) : (
                                <Link to={item.path}>{item.name}</Link>
                            )}
                        </li>
                    ))}
                </ol>
            </nav>
        </>
    );
};


export default Breadcrumb;
